import type { Image, RichText } from '@eneco/cms-types';
import { asRichText } from '@eneco/cms-types';
import { createImageUrlBuilder } from '@sanity/image-url';

import type {
  LocalizedArrayEntry,
  PortableTextBlock,
  PortableTextSpan,
  SanityImage,
  SanityImageWithAlt,
} from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * `@sanity/image-url` THROWS on a ref that doesn't match its expected shape
 * (`image-<id>-<w>x<h>-<fmt>`), rather than returning gracefully — so this
 * validates and extracts width/height up front. A ref failing this check
 * means `toImage` returns `undefined` without ever calling the builder,
 * matching the "never throw" adapter contract.
 */
const parseAssetRef = (ref: string): { width: number; height: number } | undefined => {
  const match = /^image-[a-zA-Z0-9]+-(\d+)x(\d+)-[a-zA-Z]+$/.exec(ref);
  if (!match) return undefined;
  return { width: Number(match[1]), height: Number(match[2]) };
};

/**
 * Alt text from either shape the migration bundle uses: `Image`'s internationalized array (Eneco's
 * `alt` text plugin) or `focalPointImage`'s plain string (`HeroCard.brandPrimaryImage`,
 * `ContentCard.image`, …). NOTE (simplification): for the array shape, picks the first non-empty entry
 * rather than the requesting locale's — `toImage` has no locale parameter on the shared `CmsAdapter`
 * contract. Fine while pages are fetched one-locale-at-a-time; revisit if a page ever mixes alt text
 * across languages.
 */
const firstLocalizedValue = (entries: unknown): string | undefined => {
  if (typeof entries === 'string') return entries.length > 0 ? entries : undefined;
  return Array.isArray(entries)
    ? (entries as LocalizedArrayEntry[]).find(entry => Boolean(entry?.value))?.value
    : undefined;
};

export interface ImageMapperConfig {
  projectId: string;
  dataset: string;
}

export const createToImage = ({ projectId, dataset }: ImageMapperConfig) => {
  const builder = createImageUrlBuilder({ projectId, dataset });

  return (raw: unknown): Image | undefined => {
    if (!isObject(raw)) return undefined;
    // Accept either a bare Sanity image ({ asset }) or Eneco's `imageWithAlt`
    // wrapper ({ image: { asset }, alt: [...] }).
    const wrapper = raw as SanityImageWithAlt;
    const image = (isObject(wrapper.image) ? wrapper.image : raw) as SanityImage;
    const ref = image.asset?._ref;
    if (typeof ref !== 'string' || ref.length === 0) return undefined;
    const dimensions = parseAssetRef(ref);
    if (!dimensions) return undefined;

    const src = builder.image({ asset: { _ref: ref } }).url();
    // Both levels, because both shapes exist in the dataset. `imageWithAlt` models alt as a
    // sibling of `image`; the migration bundle's own `image` object carries its own `alt` instead,
    // and the MKB home page uses that one exclusively — every image there rendered `alt=""`.
    //
    // Not only an accessibility bug. Sanity's documented way to make an image click-to-editable is
    // stega inside the `alt` attribute (there is no text node in an `<img>` to hide it in), so an
    // empty alt is also an image the editor cannot click in Presentation.
    const alt = firstLocalizedValue(wrapper.alt) ?? firstLocalizedValue((image as Record<string, unknown>).alt);
    const { width, height } = dimensions;

    return {
      src,
      ...(alt ? { alt } : {}),
      width,
      height,
      aspectRatio: `${width}/${height}`,
    };
  };
};

const escapeHtml = (text: string): string =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const escapeAttr = (value: string): string =>
  escapeHtml(value).replaceAll('"', '&quot;');

type MarkDef = NonNullable<PortableTextBlock['markDefs']>[number];

/** ` name="value"`, escaped, or nothing for an empty or missing value. */
const attribute = (name: string, value: unknown): string =>
  typeof value === 'string' && value.length > 0 ? ` ${name}="${escapeAttr(value)}"` : '';

/**
 * Render a span. A mark is either a decorator (`strong`/`em`) or a `_key`
 * referencing an entry in the block's `markDefs` (e.g. a `link`). Link marks
 * become `<a>` tags with an escaped href, and the new tab and `rel` that
 * `links.ts` gives a link that asks for one, as Sitecore writes them. A link
 * without an address keeps its words as text; unknown marks are passed through.
 */
const renderSpan = (span: PortableTextSpan, markDefs: MarkDef[]): string => {
  // `htmlToBlocks` (block-tools) turns a source `<br>` into a literal `\n` inside a span's text
  // rather than a `hardBreak` mark, so it must be restored here or it gets whitespace-collapsed away.
  const text = escapeHtml(span.text ?? '').replaceAll('\n', '<br>');
  const marks = span.marks ?? [];
  return marks.reduce((acc, mark) => {
    if (mark === 'strong') return `<strong>${acc}</strong>`;
    if (mark === 'em') return `<em>${acc}</em>`;
    const def = markDefs.find(d => d._key === mark);
    if (def?._type === 'link' && typeof def.href === 'string' && def.href.length > 0) {
      return `<a href="${escapeAttr(def.href)}"${attribute('target', def.target)}${attribute('rel', def.rel)}>${acc}</a>`;
    }
    return acc;
  }, text);
};

const renderBlock = (block: PortableTextBlock): string => {
  const markDefs = block.markDefs ?? [];
  const inner = (block.children ?? []).map(child => renderSpan(child, markDefs)).join('');
  const style = block.style ?? 'normal';
  if (style.startsWith('h') && style.length === 2) return `<${style}>${inner}</${style}>`;
  if (block.listItem) return `<li>${inner}</li>`;
  return `<p>${inner}</p>`;
};

/** Portable Text has no list container — each item is a sibling block carrying `listItem`. Emitting
 * them bare produces `<li>`s with no parent: invalid HTML, and browsers render them unstyled. So a
 * run of consecutive items of the same kind is wrapped once, and the wrapper closes as soon as the
 * kind changes or a non-list block appears.
 *
 * NOTE: `level` (nested lists) is not handled — a sub-list renders flat, inside its parent's
 * wrapper. That is still better-formed than today's output, and nesting is best fixed by moving to
 * `@portabletext/react` rather than growing this serializer (see `toRichText`'s note). */
const listTagFor = (block: PortableTextBlock): 'ol' | 'ul' | undefined => {
  if (!block.listItem) return undefined;
  return block.listItem === 'number' ? 'ol' : 'ul';
};

/** A block in the plain style — not a heading, not a list item. */
const isPlain = (block: PortableTextBlock): boolean => {
  const style = block.style ?? 'normal';
  return !block.listItem && !(style.startsWith('h') && style.length === 2);
};

/**
 * A plain block without visible text: only line breaks, spaces or nothing at all.
 *
 * The HTML→Portable Text conversion makes one where Sitecore content spaced two blocks apart: a lone
 * `<br />` between them becomes a block holding `\n`, an empty paragraph (`<p>&nbsp;</p>`,
 * `<div>&nbsp;</div>`) a block holding nothing. Such a block is that spacing, not a paragraph, so it
 * renders as line breaks (`renderSpacing`) and takes no separator on either side; with the
 * separators, the one line Sitecore shows became four or five.
 */
const isSpacing = (block: PortableTextBlock): boolean =>
  isPlain(block) && (block.children ?? []).every(child => (child.text ?? '').trim() === '');

/** A paragraph of text — not a heading, a list item or spacing. */
const isParagraph = (block: PortableTextBlock): boolean => isPlain(block) && !isSpacing(block);

/**
 * Spacing as line breaks: the ones the block holds (Sitecore's `<br />`), and one for an empty block.
 *
 * An empty paragraph does not render as `<p>&nbsp;</p>` on purpose: where the design system shows
 * rich text paragraphs inline (a `RichText` inside a `Text`), that runs the next paragraph onto the
 * same line, while a `<br>` always starts a new one. Between block paragraphs both show one empty line.
 */
const renderSpacing = (block: PortableTextBlock): string => {
  const breaks = (block.children ?? []).map(child => child.text ?? '').join('').split('\n').length - 1;
  return '<br>'.repeat(Math.max(breaks, 1));
};

/**
 * Separator between consecutive paragraphs.
 *
 * The design system renders `<p>` as `<Text as="p">` with **no margin**
 * (`@eneco/components/RichText/RichText`), so paragraphs sit flush and read as one block of text.
 * Sitecore content only looks right because authors typed literal `<br><br>` between them; the
 * HTML→Portable Text conversion drops those, leaving nothing to space migrated paragraphs apart.
 * Reproducing them here keeps the two lanes visually identical, which is what the `?_cms=` comparison
 * is for. A lone `<br />` does survive the conversion, as a block of its own: that block is the
 * spacing, so it takes no separator (`isSpacing`).
 *
 * The better fix is a margin on `Paragraph` in the shared `RichText`, but that component also serves
 * the Sitecore lane, whose content still carries its own `<br>`s — adding one there would double the
 * spacing on every existing page. Worth doing when that lane goes.
 */
const PARAGRAPH_SEPARATOR = '<br><br>';

const renderBlocks = (blocks: PortableTextBlock[]): string => {
  let html = '';
  let openList: 'ol' | 'ul' | undefined;
  let previous: PortableTextBlock | undefined;

  for (const block of blocks) {
    const tag = listTagFor(block);
    if (openList && openList !== tag) {
      html += `</${openList}>`;
      openList = undefined;
    }
    if (tag && !openList) {
      html += `<${tag}>`;
      openList = tag;
    }
    if (previous && isParagraph(previous) && isParagraph(block)) html += PARAGRAPH_SEPARATOR;
    html += isSpacing(block) ? renderSpacing(block) : renderBlock(block);
    previous = block;
  }

  return openList ? `${html}</${openList}>` : html;
};

/**
 * Serialize Portable Text into the HTML string `RichText` brand the rest of the
 * codebase expects.
 *
 * NOTE (A.5 finding): `RichText` is a branded *string*; Sanity rich text is a
 * *block array*. Serializing here is lossy — it cannot express custom Portable
 * Text components (callouts, embeds). Recommend widening `RichText` to
 * `string | PortableTextBlock[]` in Phase A.5 / I rather than serializing.
 */
export const toRichText = (raw: unknown): RichText | undefined => {
  // Already-serialized HTML string (e.g. a legacy/migrated field) passes through.
  if (typeof raw === 'string') return raw.length > 0 ? asRichText(raw) : undefined;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const blocks = raw.filter(
    (b): b is PortableTextBlock => isObject(b) && (b as { _type?: string })._type === 'block',
  );
  if (blocks.length === 0) return undefined;
  const html = renderBlocks(blocks);
  return html.length > 0 ? asRichText(html) : undefined;
};
